<template>
    <div class="docs-page">
        <div v-if="isLoading" class="loading-overlay"><p>Carregando...</p></div>

        <div v-if="msgAlerta" class="info-alert row-space" @click="msgAlerta=null">
            <span class="text">{{ msgAlerta }}</span>
        </div>

        <div v-if="estabelecimentoSelecionado" class="doc-header-card row-space">
            <div class="doc-header-main">
                <div class="doc-title">{{ estabelecimentoSelecionado.nmestabelecimento }}</div>
                <div class="doc-subtitle">{{ estabelecimentoSelecionado.nmatividade }}   -   {{ estabelecimentoSelecionado.nmsim }}</div>
            </div>


            <div class="doc-header-actions">
                <button v-if="!globalStore.idestabelecimento" class="btn-small" @click="showModalEstabelecimento = true">Alterar</button>
                <button class="btn-small" @click="closeForm">Sair</button>
            </div>
        </div>

        <div v-if="!isLoading && gruposDocumentos.length === 0" class="empty-state">
            Nenhum documento ativo encontrado para este estabelecimento.
        </div>

        <div
            v-for="grupo in gruposDocumentos"
            :key="grupo.nome"
            class="grupo-card row-space"
        >
            <div class="grupo-header" @click="toggleGrupo(grupo.nome)">
                <div>
                    <div class="grupo-title">{{ grupo.nome }}</div>
                    <div class="grupo-count">{{ grupo.itens.length }} anexo(s)</div>
                </div>

                <div class="grupo-toggle">
                    {{ grupoAberto(grupo.nome) ? '−' : '+' }}
                </div>
            </div>

            <div v-show="grupoAberto(grupo.nome)" class="grupo-body">
                <div class="explorer-grid">
                    <div
                        v-for="obj in grupo.itens"
                        :key="obj.id"
                        class="file-tile"
                        :class="classeVencimento(obj)"
                        @dblclick="lerAnexo(obj.id)"
                    >
                        <div class="file-icon">
                            {{ getIconeArquivo(obj) }}
                        </div>

                        <div class="file-name" :title="obj.nmdoc || obj.nmtpdoc || obj.tiponm">
                            {{ obj.nmdoc || obj.nmtpdoc || 'Documento sem identificação' }}
                        </div>

                        <div class="file-type">
                            {{ obj.nmtpdoc || obj.tiponm || 'Arquivo' }}
                        </div>

                        <div class="file-info">
                            <span>{{ obj.situacaonm }}</span>
                            <span>Val.: {{ formatDataBR(obj.dtvalidade) }}</span>
                        </div>

                        <div class="file-status">
                            {{ obj.stvencimento }}
                        </div>

                        <button
                            type="button"
                            class="btn-open"
                            @click.stop="lerAnexo(obj.id)"
                            :disabled="isLoadingAnexos"
                        >
                            Abrir
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <EstabelecimentoPesquisaModal
            :showModal="showModalEstabelecimento"
            @close="showModalEstabelecimento = false"
            @objSelecionado="setEstabelecimento"
        />
    </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { Api } from '@/funcoes/apiExport';
import { globalStore } from '@/globals';
import EstabelecimentoPesquisaModal from '@/components/modals/EstabelecimentoPesquisaModal.vue';

const emit = defineEmits(['close']);

const estabelecimentoSelecionado = ref(null);
const lsAnexos = ref([]);
const gruposAbertos = ref({});

const isLoading = ref(false);
const isLoadingAnexos = ref(false);
const msgAlerta = ref(null);
const showModalEstabelecimento = ref(false);

const gruposDocumentos = computed(() => {
    const map = {};

    lsAnexos.value.forEach(obj => {
        const grupo = obj.nmtpdocgrupo || 'SEM GRUPO';

        if (!map[grupo]) {
            map[grupo] = {
                nome: grupo,
                itens: []
            };
        }

        map[grupo].itens.push(obj);
    });

    return Object.values(map);
});

const closeForm = () => emit('close');

async function lerAnexo(idAnexo) {
    try {
        msgAlerta.value = null;
        isLoadingAnexos.value = true;

        const r = await Api.genericPost('/operacoes/solicitar/download', { id: idAnexo });
        if (r.bolok === 1) {
            window.open(r.download_url, '_blank');
        } else {
            msgAlerta.value = `ATENÇÃO: ${r.erro || 'Não foi possível abrir o anexo.'}`;
        }
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoadingAnexos.value = false;
    }
}

async function lerDados() {
    try {
        msgAlerta.value = null;
        isLoading.value = true;

        const dados = {
            idep: globalStore.idep,
            idsim: globalStore.idsim,
            idestabelecimento: estabelecimentoSelecionado.value.id,
            ativo: 'S',
            strparam1: 'NMTPDOCGRUPO, NMTPDOC',
            strparam2: 'PASTA'
        };

        const req = await Api.genericPost('/listas/anexos', dados);
        lsAnexos.value = req || [];

        gruposDocumentos.value.forEach(grupo => {
            gruposAbertos.value[grupo.nome] = false;
        });
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function setEstabelecimento(obj) {
    estabelecimentoSelecionado.value = obj;
    await lerDados();
}

async function startForm() {
    if (globalStore.idestabelecimento > 0) {
        estabelecimentoSelecionado.value = {
            id: globalStore.idestabelecimento,
            nmestabelecimento: globalStore.nmestabelecimento,
            nmatividade: globalStore.atividade?.nmatividade,
            cpfcnpj: globalStore.cpfcnpj,
            nmsim: globalStore.nmsim,
            nmmunicipio: globalStore.nmmunicipio,
            uf: globalStore.uf
        };

        await lerDados();
    } else {
        showModalEstabelecimento.value = true;
    }
}

function toggleGrupo(nome) {
    gruposAbertos.value[nome] = !gruposAbertos.value[nome];
}

function grupoAberto(nome) {
    return gruposAbertos.value[nome] === true;
}

function formatDataBR(value) {
    if (!value) return '-';

    const [ano, mes, dia] = value.substring(0, 10).split('-');
    if (!ano || !mes || !dia) return value;

    return `${dia}/${mes}/${ano}`;
}

function classeVencimento(obj) {
    if (obj.stvencimento === 'VENCIDO') return 'vencido';
    return 'prazo';
}

function getIconeArquivo(obj) {
    const tipo = (obj.tipo || '').toString().toUpperCase();
    const tiponm = (obj.tiponm || '').toUpperCase();

    if (tipo === 'E' || tipo === 'R' || tiponm.includes('ASSINATURA')) return '✍️';
    if (tiponm.includes('PDF') || tipo === '0') return '📄';
    if (tiponm.includes('IMAGEM') || tiponm.includes('JPG') || tiponm.includes('PNG') || tipo === '1') return '🖼️';
    if (tiponm.includes('WORD') || tiponm.includes('DOC')) return '📝';
    if (tiponm.includes('EXCEL') || tiponm.includes('XLS')) return '📊';

    return '📎';
}

onMounted(startForm);
</script>

<style scoped>
.docs-page {
    width: 100%;
    text-align: left;
}

.doc-header-card {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    background: #fff;
    border: 1px solid #d9e2ec;
    border-radius: 12px;
    padding: 14px 16px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.doc-header-main {
    text-align: left;
}

.doc-title {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
}

.doc-subtitle {
    font-size: 13px;
    color: #64748b;
    margin-top: 3px;
}

.doc-header-actions {
    display: flex;
    gap: 8px;
    align-items: flex-start;
}

.grupo-card {
    border: 1px solid #d9e2ec;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.grupo-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 14px;
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;
    cursor: pointer;
}

.grupo-header:hover {
    background: #f1f5f9;
}

.grupo-title {
    font-size: 15px;
    font-weight: 700;
    color: #1f2937;
    text-align: left;
}

.grupo-count {
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
}

.grupo-toggle {
    font-size: 24px;
    color: #334155;
    line-height: 1;
}

.grupo-body {
    padding: 14px;
}

.explorer-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
}

.file-tile {
    position: relative;
    min-height: 150px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    padding: 10px 12px;
    cursor: default;
    text-align: center;
    transition: 0.12s ease;
}

.file-tile:hover {
    background: #f8fafc;
    border-color: #94a3b8;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.file-tile.vencido {
    border-top: 5px solid #dc2626;
}

.file-tile.prazo {
    border-top: 5px solid #16a34a;
}

.file-icon {
    font-size: 36px;
    line-height: 1;
    margin-top: 2px;
    margin-bottom: 6px;
    text-align: center;
}

.file-name {
    font-size: 13px;
    font-weight: 700;
    color: #1f2937;
    text-align: center;
    line-height: 1.15;

    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    overflow: hidden;
    min-height: 30px;
}

.file-type {
    font-size: 11px;
    color: #64748b;
    text-align: center;
    margin-top: 3px;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.file-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 5px;
    font-size: 10px;
    color: #64748b;
    text-align: center;
}

.file-info span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.file-status {
    margin-top: 5px;
    font-size: 10px;
    font-weight: 700;
    text-align: center;
    color: #334155;
    background: #f1f5f9;
    border-radius: 999px;
    padding: 3px 6px;
}

.btn-open {
    width: auto !important;
    min-width: 70px;
    margin-top: 6px;
    padding: 4px 10px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #f8fafc;
    color: #334155;
    cursor: pointer;
    font-size: 12px;
}

.btn-open:hover {
    background: #e2e8f0;
}

.btn-open:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.empty-state {
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    border-radius: 10px;
    padding: 22px;
    color: #64748b;
    text-align: center;
    font-size: 13px;
}

.docs-page button {
    text-align: center;
}

@media (max-width: 700px) {
    .doc-header-card {
        flex-direction: column;
    }

    .doc-header-actions {
        justify-content: flex-start;
    }

    .explorer-grid {
        grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    }
}
</style>